import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  Text,
  VStack,
  HStack,
  Spinner,
  Center,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
} from '@chakra-ui/react';
import {
  SearchIcon,
  CheckIcon,
  CloseIcon,
  WarningIcon,
} from '@chakra-ui/icons';
import {
  MdAttachMoney,
  MdAutoFixHigh,
  MdThumbUp,
  MdThumbDown,
  MdPerson,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';
import LogPaymentModal from '../components/Payments/LogPaymentModal';

interface Payment {
  id: string;
  landlordId: string;
  tenantId?: string;
  tenantName?: string;
  propertyAddress?: string;
  amount: number;
  expectedAmount?: number;
  paymentDate: any;
  status: 'pending' | 'confirmed' | 'reconciled' | 'unmatched';
  method?: string;
  reference?: string;
  senderName?: string;
  senderEmail?: string;
  senderPhone?: string;
  interacMessage?: string;
  reconciled?: boolean;
  needsReview?: boolean;
  confidence?: number;
  matchMethod?: 'ai' | 'rule' | 'manual';
  matchedAt?: any;
  suggestedTenantId?: string;
  suggestedTenantName?: string;
  notes?: string;
}

interface Tenant {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  propertyAddress?: string;
  unitNumber?: string;
  expectedRent?: number;
}

interface PaymentStats {
  total: number;
  matched: number;
  unmatched: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
}

const PaymentsPage: React.FC = () => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [payments, setPayments] = useState<Payment[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<PaymentStats>({
    total: 0,
    matched: 0,
    unmatched: 0,
    highConfidence: 0,
    mediumConfidence: 0,
    lowConfidence: 0,
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [triggeringAutoMatch, setTriggeringAutoMatch] = useState(false);
  const { isOpen: isLogPaymentOpen, onOpen: onLogPaymentOpen, onClose: onLogPaymentClose } = useDisclosure();

  useEffect(() => {
    fetchPayments();
    fetchTenants();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [payments]);

  const fetchPayments = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getPayments`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast({
        title: 'Error loading payments',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getTenants`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const calculateStats = () => {
    const unmatchedPayments = payments.filter(p => !p.reconciled || p.needsReview);
    
    setStats({
      total: payments.length,
      matched: payments.filter(p => p.reconciled && !p.needsReview).length,
      unmatched: unmatchedPayments.length,
      highConfidence: unmatchedPayments.filter(p => (p.confidence || 0) >= 80).length,
      mediumConfidence: unmatchedPayments.filter(p => (p.confidence || 0) >= 50 && (p.confidence || 0) < 80).length,
      lowConfidence: unmatchedPayments.filter(p => (p.confidence || 0) < 50).length,
    });
  };

  const getConfidenceColor = (confidence?: number): string => {
    if (!confidence) return 'gray';
    if (confidence >= 80) return 'green';
    if (confidence >= 50) return 'yellow';
    return 'red';
  };

  const getConfidenceLabel = (confidence?: number): string => {
    if (!confidence) return 'No Match';
    if (confidence >= 80) return 'High';
    if (confidence >= 50) return 'Medium';
    return 'Low';
  };

  const acceptMatch = async (paymentId: string) => {
    if (!user) return;
    
    try {
      setProcessing(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/acceptPaymentMatch`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ paymentId }),
        }
      );

      if (!response.ok) throw new Error('Failed to accept match');

      toast({
        title: 'Match Accepted',
        description: 'Payment successfully matched to tenant.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await fetchPayments();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to accept match. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  const openManualMatch = (payment: Payment) => {
    setSelectedPayment(payment);
    setSelectedTenantId('');
    onOpen();
  };

  const manualMatch = async () => {
    if (!user || !selectedPayment || !selectedTenantId) return;
    
    try {
      setProcessing(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/manualPaymentMatch`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentId: selectedPayment.id,
            tenantId: selectedTenantId,
          }),
        }
      );

      if (!response.ok) throw new Error('Failed to manually match');

      toast({
        title: 'Match Saved',
        description: 'Payment manually matched to tenant.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
      await fetchPayments();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save manual match. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(false);
    }
  };

  const triggerAutoMatch = async () => {
    if (!user) return;
    
    try {
      setTriggeringAutoMatch(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/runPaymentReconciliation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to trigger auto-match');

      const data = await response.json();
      
      toast({
        title: 'Auto-Match Complete',
        description: `Processed ${data.processed} payments. ${data.matched} matched successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      await fetchPayments();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to trigger auto-match. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTriggeringAutoMatch(false);
    }
  };

  const filteredPayments = useMemo(() => {
    return payments
      .filter(p => !p.reconciled || p.needsReview)
      .filter(p => {
        // Search filter
        const matchesSearch = !searchTerm || 
          p.senderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.senderEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.interacMessage?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.suggestedTenantName?.toLowerCase().includes(searchTerm.toLowerCase());

        // Confidence filter
        const matchesConfidence = confidenceFilter === 'all' ||
          (confidenceFilter === 'high' && (p.confidence || 0) >= 80) ||
          (confidenceFilter === 'medium' && (p.confidence || 0) >= 50 && (p.confidence || 0) < 80) ||
          (confidenceFilter === 'low' && (p.confidence || 0) < 50);

        return matchesSearch && matchesConfidence;
      })
      .sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }, [payments, searchTerm, confidenceFilter]);

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Payment Matching</Heading>
        <HStack spacing={3}>
          <Button
            colorScheme="green"
            leftIcon={<Icon as={MdAttachMoney} />}
            onClick={onLogPaymentOpen}
          >
            Log Manual Payment
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<Icon as={MdAutoFixHigh} />}
            onClick={triggerAutoMatch}
            isLoading={triggeringAutoMatch}
            loadingText="Matching..."
          >
            Run Auto-Match Now
          </Button>
        </HStack>
      </Flex>

      {/* Log Payment Modal */}
      <LogPaymentModal
        isOpen={isLogPaymentOpen}
        onClose={onLogPaymentClose}
        tenants={tenants}
        propertyId=""
        onPaymentLogged={() => {
          fetchPayments();
        }}
      />

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Total Payments</StatLabel>
              <StatNumber>{stats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Matched</StatLabel>
              <StatNumber color="green.500">{stats.matched}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Unmatched</StatLabel>
              <StatNumber color="orange.500">{stats.unmatched}</StatNumber>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>High Confidence</StatLabel>
              <StatNumber color="green.500">{stats.highConfidence}</StatNumber>
              <StatHelpText>≥80%</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Medium Confidence</StatLabel>
              <StatNumber color="yellow.500">{stats.mediumConfidence}</StatNumber>
              <StatHelpText>50-79%</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Low Confidence</StatLabel>
              <StatNumber color="red.500">{stats.lowConfidence}</StatNumber>
              <StatHelpText>&lt;50%</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
            <InputGroup flex={1}>
              <InputLeftElement>
                <SearchIcon color="gray.500" />
              </InputLeftElement>
              <Input
                placeholder="Search by sender, email, message, or tenant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              value={confidenceFilter}
              onChange={(e) => setConfidenceFilter(e.target.value)}
              w={{ base: 'full', md: '200px' }}
            >
              <option value="all">All Confidence Levels</option>
              <option value="high">High (≥80%)</option>
              <option value="medium">Medium (50-79%)</option>
              <option value="low">Low (&lt;50%)</option>
            </Select>
          </Flex>
        </CardBody>
      </Card>

      {/* Unmatched Payments Table */}
      <Card>
        <CardHeader>
          <Heading size="md">Unmatched Payments ({filteredPayments.length})</Heading>
          <Text fontSize="sm" color="gray.600" mt={2}>
            Review AI-suggested matches and approve or manually assign payments to tenants.
          </Text>
        </CardHeader>
        <CardBody>
          {filteredPayments.length === 0 ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <AlertTitle>All Caught Up!</AlertTitle>
              <AlertDescription>
                No unmatched payments at this time.
              </AlertDescription>
            </Alert>
          ) : (
            <VStack spacing={4} align="stretch">
              {filteredPayments.map((payment) => (
                <Card key={payment.id} variant="outline">
                  <CardBody>
                    <Flex direction={{ base: 'column', lg: 'row' }} gap={4} justify="space-between">
                      <Box flex={1}>
                        <HStack mb={2}>
                          <Icon as={MdAttachMoney} color="green.500" boxSize={6} />
                          <Text fontSize="xl" fontWeight="bold">
                            ${payment.amount.toFixed(2)}
                          </Text>
                          {payment.confidence !== undefined && (
                            <Badge colorScheme={getConfidenceColor(payment.confidence)}>
                              {getConfidenceLabel(payment.confidence)} ({payment.confidence}%)
                            </Badge>
                          )}
                          {payment.matchMethod && (
                            <Badge colorScheme="blue">
                              {payment.matchMethod === 'ai' && <Icon as={MdAutoFixHigh} mr={1} />}
                              {payment.matchMethod.toUpperCase()}
                            </Badge>
                          )}
                        </HStack>

                        {payment.confidence !== undefined && (
                          <Tooltip
                            label={`Confidence: ${payment.confidence}% - Factors: Name similarity, amount match, timing`}
                            placement="top"
                            hasArrow
                          >
                            <Box>
                              <Progress 
                                value={payment.confidence} 
                                colorScheme={getConfidenceColor(payment.confidence)}
                                size="sm"
                                mb={2}
                                borderRadius="md"
                              />
                            </Box>
                          </Tooltip>
                        )}

                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Text fontSize="sm" color="gray.600" fontWeight="medium">From:</Text>
                            <Text fontSize="sm">
                              {payment.senderName || payment.senderEmail || 'Unknown'}
                            </Text>
                          </HStack>

                          {payment.interacMessage && (
                            <HStack>
                              <Text fontSize="sm" color="gray.600" fontWeight="medium">Message:</Text>
                              <Text fontSize="sm" fontStyle="italic">"{payment.interacMessage}"</Text>
                            </HStack>
                          )}

                          {payment.suggestedTenantName && (
                            <HStack>
                              <Icon as={MdPerson} color="blue.500" />
                              <Text fontSize="sm" color="gray.600" fontWeight="medium">Suggested Match:</Text>
                              <Text fontSize="sm" fontWeight="bold" color="blue.600">
                                {payment.suggestedTenantName}
                              </Text>
                            </HStack>
                          )}

                          <Text fontSize="xs" color="gray.500">
                            Received: {new Date(payment.paymentDate).toLocaleDateString()}
                          </Text>
                        </VStack>
                      </Box>

                      <VStack spacing={2} minW="200px">
                        {payment.suggestedTenantId && payment.confidence && payment.confidence >= 50 ? (
                          <>
                            <Button
                              colorScheme="green"
                              leftIcon={<CheckIcon />}
                              size="sm"
                              w="full"
                              onClick={() => acceptMatch(payment.id)}
                              isLoading={processing}
                            >
                              Accept Match
                            </Button>
                            <Button
                              variant="outline"
                              leftIcon={<CloseIcon />}
                              size="sm"
                              w="full"
                              onClick={() => openManualMatch(payment)}
                            >
                              Assign Manually
                            </Button>
                          </>
                        ) : (
                          <Button
                            colorScheme="blue"
                            leftIcon={<Icon as={MdPerson} />}
                            size="sm"
                            w="full"
                            onClick={() => openManualMatch(payment)}
                          >
                            Match to Tenant
                          </Button>
                        )}
                      </VStack>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Manual Match Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manually Match Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPayment && (
              <VStack spacing={4} align="stretch">
                <Card variant="outline">
                  <CardBody>
                    <HStack mb={2}>
                      <Icon as={MdAttachMoney} color="green.500" boxSize={5} />
                      <Text fontSize="lg" fontWeight="bold">
                        ${selectedPayment.amount.toFixed(2)}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      From: {selectedPayment.senderName || selectedPayment.senderEmail || 'Unknown'}
                    </Text>
                    {selectedPayment.interacMessage && (
                      <Text fontSize="sm" color="gray.600" fontStyle="italic">
                        "{selectedPayment.interacMessage}"
                      </Text>
                    )}
                  </CardBody>
                </Card>

                <FormControl>
                  <FormLabel>Select Tenant</FormLabel>
                  <Select
                    placeholder="Choose a tenant..."
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                  >
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.firstName} {tenant.lastName} - {tenant.propertyAddress}
                        {tenant.expectedRent && ` ($${tenant.expectedRent}/mo)`}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={manualMatch}
              isLoading={processing}
              isDisabled={!selectedTenantId}
            >
              Assign Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PaymentsPage;
